import { html, TemplateResult } from 'lit';
import { WEB_COMPONENT_EASM_ASSETS_INVENTORY } from '../../../../webcomponents.constants';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { translateFn } from '../../../../utils/translator.helper';
import { formatLargeNumber } from '../../../../utils/formatter.utils';
import { EasmAssetsInventoryPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { createAssetsInventoryColumns } from '../utils';
import { store } from '../../../../redux/store';
import { selectDashboardFilterValues } from '../../../../genericDashboard/redux/dashboard.filters.slice';
import { GenericDashboardsFiltersHelper } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { generateFiltersForInventory } from '../../../utils/utils';
import { FilterType } from '../../genericTopData/types/types';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { FindingType } from '../../../types';

@customElement(WEB_COMPONENT_EASM_ASSETS_INVENTORY)
export class GzEasmAssetsInventoryWebComponent extends GzGenericWidgetContent {
  @property({
    type: WidgetStateFrontEnd,
    attribute: true,
    reflect: true,
  })
  state: WidgetStateFrontEnd;

  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  payload: EasmAssetsInventoryPayload;

  static get styles() {
    return styles;
  }

  protected renderLoader(): TemplateResult {
    return html` <gz-loader rows="1" columns="3"></gz-loader>`;
  }
  protected renderContent(): TemplateResult {
    const easmAssetsInventory = this.payload?.easmAssetsInventory;
    const emptyString = '--';
    const columns = createAssetsInventoryColumns(
      [
        {
          firstLabel: translateFn('Domains'),
          firstValue:
            this.state === WidgetStateFrontEnd.IS_ACTIVE
              ? formatLargeNumber(easmAssetsInventory?.domains?.total)
              : emptyString,
          firstInventorySmartViewId: easmAssetsInventory?.domains?.inventorySmartViewId as string,
          firstFilterType: FilterType.NONE,
          firstFilterDataArray: [],
        },
        {
          firstLabel: translateFn('IP addresses'),
          firstValue:
            this.state === WidgetStateFrontEnd.IS_ACTIVE
              ? formatLargeNumber(easmAssetsInventory?.ipAddresses?.total)
              : emptyString,
          firstInventorySmartViewId: easmAssetsInventory?.ipAddresses?.inventorySmartViewId as string,
          firstFilterType: FilterType.FINDING_TYPE,
          firstFilterDataArray: [FindingType.IP_6, FindingType.IP_4],
        },
        {
          firstLabel: translateFn('Emails'),
          firstValue:
            this.state === WidgetStateFrontEnd.IS_ACTIVE
              ? formatLargeNumber(easmAssetsInventory?.emails?.total)
              : emptyString,
          firstInventorySmartViewId: easmAssetsInventory?.emails?.inventorySmartViewId as string,
          firstFilterType: FilterType.FINDING_TYPE,
          firstFilterDataArray: [FindingType.EMAIL],
        },
      ],
      this.navigateToInventory.bind(this)
    );
    return html` <div id="easm-assets-inventory-container">${columns}</div> `;
  }

  /**
   * Navigate to inventory
   * @param inventorySmartViewId smart view id used to navigate to inventory and select the correct view
   * @param filterType filter type used to create parameters for filters
   * @param filterData filter data used to create parameters for filters
   * @param filterDataArray filter data array used to create parameters for filters
   * @protected
   */
  protected async navigateToInventory(
    inventorySmartViewId: string,
    filterType: FilterType,
    filterData: string,
    filterDataArray: Array<FindingType> = []
  ): Promise<void> {
    if (this.state !== WidgetStateFrontEnd.IS_ACTIVE) {
      return;
    }

    const dashboardAppState = store.getState().dashboardsApp;
    const filters = selectDashboardFilterValues(dashboardAppState);
    const companyId = GenericDashboardsFiltersHelper.getCompanyIdFromSingleSelectCompanyFilter(filters) as string;

    const filtersToSend = generateFiltersForInventory(filterType, filterData, companyId, filterDataArray);

    await NavigationHelper.navigateToEASMAssetsInventory({
      smartViewId: inventorySmartViewId,
      filters: filtersToSend,
    });
  }
}
